// Math PURE des gestes de l'éditeur (zoom/pan au pincement, Phase 3 L2). Aucune dépendance DOM :
// prend des nombres, renvoie une `View`. Testable en Node pur (comme client/camera.ts). Le câblage
// des Pointer Events vit dans client/editor.ts.
//
// Modèle : `View {scale, tx, ty}` = transformation CSS `translate(tx,ty) scale(scale)` appliquée au
// canvas avec `transform-origin: 0 0`. Un point `p` du repère LOCAL naturel du canvas (px CSS,
// relatifs à son coin haut-gauche non transformé) s'affiche à l'écran en `p·scale + t`.

export interface View { scale: number; tx: number; ty: number }

/** Échelle minimale : 1 = vue naturelle (pas de dézoom en dessous, la carte remplit déjà le cadre). */
export const MIN_SCALE = 1
/** Échelle maximale : 6× (assez pour viser une case sur une grande carte 56×32 au doigt). */
export const MAX_SCALE = 6

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v))

/** Distance euclidienne entre deux points. */
export function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(bx - ax, by - ay)
}

/** Milieu de deux points. */
export function midpoint(ax: number, ay: number, bx: number, by: number): { x: number; y: number } {
  return { x: (ax + bx) / 2, y: (ay + by) / 2 }
}

/**
 * Zoom autour d'un point focal (le milieu des deux doigts). `focusX`/`focusY` sont dans le MÊME
 * repère que `tx`/`ty` : px écran relatifs au coin haut-gauche NATUREL du canvas (cf. `nlx` dans
 * editor.ts), PAS des coordonnées de contenu non scalé. L'échelle finale = `start.scale · ratio`
 * bornée à [MIN, MAX] ; le déplacement est recalculé pour que le point de contenu sous le focus
 * reste EXACTEMENT sous le focus après le zoom (bornage d'échelle inclus).
 */
export function pinch(start: View, focusX: number, focusY: number, ratio: number): View {
  const scale = clamp(start.scale * ratio, MIN_SCALE, MAX_SCALE)
  // Point de contenu (repère non transformé) actuellement sous le focus : c = (focus - t) / scale.
  // On veut c·scaleFinal + tFinal == focus, d'où tFinal = focus - c·scaleFinal.
  const tx = focusX - ((focusX - start.tx) / start.scale) * scale
  const ty = focusY - ((focusY - start.ty) / start.scale) * scale
  return { scale, tx, ty }
}

/** Déplacement : décale la vue de (dx, dy) px écran (l'échelle ne change pas). */
export function pan(v: View, dx: number, dy: number): View {
  return { scale: v.scale, tx: v.tx + dx, ty: v.ty + dy }
}

/**
 * Borne une vue : échelle dans [MIN, MAX], puis déplacement borné pour que le contenu scalé
 * (natW·scale × natH·scale) couvre toujours le cadre naturel (natW × natH) — aucun vide au bord.
 * À l'échelle 1, cela force tx=ty=0 (vue recadrée = identité, canvas en position naturelle).
 * `natW`/`natH` = dimensions CSS naturelles du canvas (offsetWidth/offsetHeight, indépendantes du transform).
 */
export function clampView(v: View, natW: number, natH: number): View {
  const scale = clamp(v.scale, MIN_SCALE, MAX_SCALE)
  // Bornes : tx ∈ [natW·(1-scale), 0] (à scale≥1 la borne min est ≤ 0). Idem ty.
  const tx = clamp(v.tx, natW * (1 - scale), 0)
  const ty = clamp(v.ty, natH * (1 - scale), 0)
  return { scale, tx, ty }
}

/* ════════════════════ LA DÉCISION D'AXE DU JOYSTICK (31/07) ════════════════════
 * Le jeu n'a pas de diagonale : `moveRunner` traite le vertical et l'horizontal séparément. Le
 * joystick lit tous les angles et doit donc TRANCHER sur un axe.
 *
 * ⚠️ TRANCHER À 45° EXACTEMENT FAIT BÉGAYER LE PERSONNAGE. Première version : `|dx| >= |dy|`.
 * Pile sur la diagonale, un tremblement de pouce d'un pixel fait alterner « droite » et « haut »
 * plusieurs fois par seconde. Or c'est le geste le plus fréquent du jeu — courir vers la droite
 * puis pousser vers le haut à l'échelle passe forcément par la diagonale. On ratait un mouvement
 * qu'on avait pourtant bien fait.
 *
 * D'où l'HYSTÉRÉSIS : une fois un axe tenu, il faut dépasser FRANCHEMENT la diagonale pour en
 * changer (BIAIS = 1,5, soit ~56° au lieu de 45°). On engage aussi facilement qu'avant, on ne
 * décroche plus par accident. Quitter une échelle reste immédiat : pousser à l'horizontale pure
 * donne |dy| ≈ 0, la condition est franchie d'emblée.
 */
export type AxeDir = 'up' | 'down' | 'left' | 'right'

/** Combien l'autre axe doit dépasser celui qu'on tient déjà pour le remplacer. */
export const BIAIS_AXE = 1.5

/**
 * @param dx,dy  écart du pouce au centre du joystick, en px
 * @param tenu   l'axe actuellement retenu (null = neutre)
 * @param morte  rayon de la zone morte centrale
 */
export function axeDominant(dx: number, dy: number, tenu: AxeDir | null, morte: number): AxeDir | null {
  if (Math.hypot(dx, dy) < morte) return null
  const h = Math.abs(dx), v = Math.abs(dy)
  const horizontalTenu = tenu === 'left' || tenu === 'right'
  const verticalTenu = tenu === 'up' || tenu === 'down'
  const horizontal = horizontalTenu ? v <= h * BIAIS_AXE
    : verticalTenu ? h > v * BIAIS_AXE
    : h >= v
  return horizontal ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down')
}

/* ════════════════ L'AXE SECONDAIRE — LA NAGE SEULEMENT (06/08) ════════════════
 *
 * Tout ce qui précède part d'un fait vrai : « le jeu n'a pas de diagonale ». Il l'est pour la
 * marche, l'échelle et la corde. Il est FAUX pour l'eau — la branche nage de `moveRunner` lit
 * gauche/droite ET haut/bas du même tick, c'est la seule mécanique bi-axiale du jeu.
 *
 * Le joystick en cachait donc la moitié, et ça se mesurait : sortir d'un bassin par un rebord
 * demande 1,9 s au clavier (haut + droite tenus ensemble) et restait impossible au pouce après
 * 10 s d'essais ; nager à l'horizontale, faute de pouvoir tenir « haut » en même temps, faisait
 * couler d'une case toutes les deux secondes.
 *
 * L'axe secondaire ne REMPLACE jamais l'axe dominant — il s'y ajoute, et seulement dans l'eau. Le
 * seuil est volontairement haut : à 40 % de la course, il faut un vrai geste en biais. Sous ce
 * seuil on retrouve exactement le comportement d'avant, y compris son hystérésis, ce qui évite de
 * transformer chaque dérapage de pouce en diagonale involontaire.
 */

/** Part de la course du pommeau qu'il faut donner à l'autre axe pour qu'il compte, en nageant. */
export const SEUIL_SECONDAIRE = 0.4

/**
 * L'axe PERPENDICULAIRE au principal, s'il est poussé assez franchement (sinon null).
 *
 * @param dx,dy      écart du pouce au centre du joystick, en px
 * @param principal  l'axe déjà retenu par `axeDominant` (null = zone morte : rien à compléter)
 * @param seuil      distance minimale sur l'autre axe, en px
 */
export function axeSecondaire(dx: number, dy: number, principal: AxeDir | null, seuil: number): AxeDir | null {
  if (!principal) return null
  // On lit l'autre axe QUE CE SOIT le principal ou pas : c'est ce qui garantit qu'on ne renvoie
  // jamais deux directions du même axe (« left » + « right » n'a aucun sens pour le moteur).
  const horizontalTenu = principal === 'left' || principal === 'right'
  const autre = horizontalTenu ? dy : dx
  if (Math.abs(autre) < seuil) return null
  return horizontalTenu ? (autre < 0 ? 'up' : 'down') : (autre < 0 ? 'left' : 'right')
}

/* ════════════════ QUI TIENT LE JOYSTICK (04/08) ════════════════
 * Bug rapporté par Claude-Alain : « le joystick reste bloqué dans une direction gauche ou droite,
 * et cela oblige à quitter le jeu en forçant ». REPRODUIT : il suffit qu'un `pointerup` se perde.
 *
 * Sur un téléphone, un doigt peut disparaître SANS événement de relâche : geste système au bord de
 * l'écran (et le joystick est justement dans le coin bas-gauche), notification, appel, bascule
 * d'application, onglet mis en veille. Le stick gardait alors son `pointerId` pour toujours, la
 * dernière direction restait vraie, et surtout `pointerdown` REJETAIT tout nouveau doigt — plus
 * aucun moyen de reprendre la main, d'où « quitter le jeu en forçant ».
 *
 * La règle tient en une phrase : UN POINTEUR QUI N'EXISTE PLUS NE PEUT PAS GARDER LE STICK.
 */

/**
 * Faut-il donner le stick à ce nouveau doigt ?
 *
 * @param tenuPar    le pointeur qui tient le stick (-1 = personne)
 * @param captureVive vrai si ce pointeur a ENCORE la capture (donc s'il existe encore)
 *
 * Deux exigences opposées, et c'est tout l'intérêt de trancher ici plutôt qu'en ligne :
 *  · un SECOND doigt posé pendant qu'on joue ne doit PAS voler le stick (paume, deuxième main) ;
 *  · un doigt posé après une relâche PERDUE doit le reprendre, sinon le jeu est fini.
 * `captureVive` distingue les deux : elle est fausse dès que le navigateur a repris le pointeur.
 */
export function accepteNouveauPointeur(tenuPar: number, captureVive: boolean): boolean {
  return tenuPar === -1 || !captureVive
}
